import { useAppSelector } from 'app/hooks';
import Logo from 'assets/icons/Logo_header.svg';
import routes from 'constants/routes';
import { useFeatureFlags, useSupportActions } from 'hooks';
import { Container } from 'layout/App';
import { selectUserInfo, selectUserRole } from 'modules/auth/userSlice';
import * as Layout from 'modules/header/components/Layout';
import { UserMenu } from 'modules/header/components/UserMenu';
import { useModal } from 'modules/modals/ModalProvider';
import {
  isCurrentCustomPaidSubscriptionPlan,
  selectSubscriptionName,
} from 'modules/subscriptionsPlans/subscriptionsPlansSlice';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import colors from 'theme/colors';
import { TagBadge, Text } from 'ui';
import { isAdmin, isSiteUser } from 'utils/helpers';
import { SearchBar } from 'modules/search';
import { TEST_IDS } from 'mocks/ids';
import { useIsMobile } from 'hooks/useIsMobile';
import ThreadsBot from 'modules/threadsBot/components';
import { CirclePlay, Info, MessageCircleWarning } from 'lucide-react';
import NotificationToggle from '../notification';
import { Banner } from './components/Banner';
import ProfileMenu from './components/ProfileMenu';
import Search from './components/Search';
import { HeaderButton } from './components/ThreadsButtons';
import SupportDropdown from './components/SupportDropdown';

const Header: React.FC<{ upgradingEnabled: boolean }> = ({ upgradingEnabled }) => {
  const navigation = useNavigate();

  const { open } = useModal();

  const isMobile = useIsMobile();

  const { firstName, lastName, isImpersonate, isSiteOwner } = useAppSelector(selectUserInfo);
  const userRole = useAppSelector(selectUserRole);
  const currentSubscriptionName = useAppSelector(selectSubscriptionName);
  const isCustomPaidSubscription = useAppSelector(isCurrentCustomPaidSubscriptionPlan);
  const { features } = useFeatureFlags();

  const canUpgrade =
    isSiteOwner && !!currentSubscriptionName && !isCustomPaidSubscription && upgradingEnabled;

  const handleUpgradeClick = () => {
    navigation(`${routes.subscriptionsPlans}/dashboard`);
  };

  const { navigateToSupport, restartWalkthrough, openYbugReport } = useSupportActions();

  const supportActions = [
    {
      id: 'help',
      title: 'Help',
      icon: <Info size={24} />,
      action: navigateToSupport,
      isShown: true,
      topDivider: isMobile,
    },
    {
      id: 'walkthrough',
      title: 'Walkthrough',
      icon: <CirclePlay size={24} />,
      action: restartWalkthrough,
      isShown: true,
    },
    {
      id: 'report-issue',
      title: 'Report an Issue',
      icon: <MessageCircleWarning size={24} />,
      action: openYbugReport,
      isShown: true,
    },
  ];

  return (
    <Layout.Header id="app-header">
      <Banner />
      <Container>
        <div className="flex items-center justify-between md:flex-wrap">
          <div className="flex items-center gap-4 md:flex-grow md:flex-row">
            <Link
              to="/"
              className="aspect-square h-10 w-10 md:h-full"
              data-testid={TEST_IDS.primary_logo_link}
            >
              <img src={Logo} alt="logo" className="h-full w-full" />
            </Link>
            {features['boeing-tdp'] ? (
              <SearchBar />
            ) : (
              <Search
                data-cy="header-search"
                name="search"
                placeholder="Search..."
                aria-label="search"
              />
            )}

            {(isAdmin(userRole) || isSiteUser(userRole)) && (
              <HeaderButton open={open} features={features} className="hidden lg:flex " />
            )}
          </div>
          <div id="header-content-container" className="flex flex-row items-center gap-3 lg:gap-0">
            <div className="flex flex-nowrap items-center justify-start gap-2">
              {!isMobile && <SupportDropdown supportActions={supportActions} />}

              {features.copilot && <ThreadsBot />}

              <NotificationToggle />

              {!isMobile && canUpgrade && (
                <Layout.UpgradeButton onClick={handleUpgradeClick}>Upgrade</Layout.UpgradeButton>
              )}
            </div>

            {features['ui-library'] ? (
              <UserMenu />
            ) : (
              <div className="relative m-0 flex h-auto w-auto flex-nowrap items-center justify-start gap-2 p-0">
                {!isMobile && (
                  <Text
                    aria-labelledby="accessible-user-info"
                    size="md"
                    className="ml-5 mr-1 flex max-h-[50px] max-w-48 justify-start truncate"
                    data-cy="header-user-info"
                  >
                    {isImpersonate && (
                      <TagBadge size="xs" color={colors.orange}>
                        Impersonating
                      </TagBadge>
                    )}
                    {firstName} {lastName}
                  </Text>
                )}

                <ProfileMenu
                  isMobile={isMobile}
                  canUpgrade={canUpgrade}
                  supportActions={supportActions}
                />
              </div>
            )}
          </div>
        </div>
      </Container>
    </Layout.Header>
  );
};

export default Header;
